'use client';

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { DepartureDateEntry } from '@/services/ProductTourDataService';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';

const MONTH_NAMES: Record<string, string[]> = {
    tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};

const WEEKDAY_NAMES: Record<string, string[]> = {
    tr: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
};

function getMonthKey(year: number, month: number): string {
    return `${year}-${String(month).padStart(2, '0')}`;
}

function getDaysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
    return new Date(year, month - 1, 1).getDay();
}

function formatDateLabel(dateStr: string, lang: string): string {
    const [y, m, d] = dateStr.split('-').map(Number);
    const months = MONTH_NAMES[lang] ?? MONTH_NAMES.en;
    return `${d} ${months[m - 1]} ${y}`;
}

type ViewMode = 'calendar' | 'list';

interface DepartureDatesCalendarProps {
    departureDates: DepartureDateEntry[];
    /** Section title - boş bırakılırsa üstte başlık gösterilmez (sayfa kendi başlığını kullanır) */
    title?: string;
    /** Dil kodu (tr/en) - ay ve gün adları için. Verilmezse context'ten alınır. */
    language?: string;
}

export default function DepartureDatesCalendar({ departureDates, title, language: propLanguage }: DepartureDatesCalendarProps) {
    const { getVariable, language: contextLanguage } = useLanguageVariables();
    const lang = (propLanguage || contextLanguage || 'en').startsWith('tr') ? 'tr' : 'en';
    const monthNames = MONTH_NAMES[lang] ?? MONTH_NAMES.en;
    const weekdayNames = WEEKDAY_NAMES[lang] ?? WEEKDAY_NAMES.en;

    const resolvedTitle = title ?? getVariable('departure_dates', 'Departure Dates');
    const dateSetByKey = useMemo(() => {
        const map = new Map<string, DepartureDateEntry['status']>();
        departureDates.forEach(({ date, status }) => map.set(date, status ?? 'available'));
        return map;
    }, [departureDates]);

    const { minYear, minMonth, maxYear, maxMonth, initialYear, initialMonth } = useMemo(() => {
        if (departureDates.length === 0) {
            const now = new Date();
            return {
                minYear: now.getFullYear(),
                minMonth: now.getMonth() + 1,
                maxYear: now.getFullYear(),
                maxMonth: now.getMonth() + 1,
                initialYear: now.getFullYear(),
                initialMonth: now.getMonth() + 1,
            };
        }
        const sorted = [...departureDates].sort((a, b) => a.date.localeCompare(b.date));
        const first = sorted[0].date;
        const last = sorted[sorted.length - 1].date;
        const [minY, minM] = first.split('-').map(Number);
        const [maxY, maxM] = last.split('-').map(Number);
        return {
            minYear: minY,
            minMonth: minM,
            maxYear: maxY,
            maxMonth: maxM,
            initialYear: minY,
            initialMonth: minM,
        };
    }, [departureDates]);

    const [currentYear, setCurrentYear] = useState(initialYear);
    const [currentMonth, setCurrentMonth] = useState(initialMonth);
    const [viewMode, setViewMode] = useState<ViewMode>('calendar');

    const canPrev = currentYear > minYear || (currentYear === minYear && currentMonth > minMonth);
    const canNext = currentYear < maxYear || (currentYear === maxYear && currentMonth < maxMonth);

    const goPrev = () => {
        if (currentMonth === 1) {
            setCurrentYear((y) => y - 1);
            setCurrentMonth(12);
        } else {
            setCurrentMonth((m) => m - 1);
        }
    };

    const goNext = () => {
        if (currentMonth === 12) {
            setCurrentYear((y) => y + 1);
            setCurrentMonth(1);
        } else {
            setCurrentMonth((m) => m + 1);
        }
    };

    const calendarDays = useMemo(() => {
        const daysInMonth = getDaysInMonth(currentYear, currentMonth);
        const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
        const cells: Array<{ day: number | null; dateKey: string | null; status?: DepartureDateEntry['status'] }> = [];
        for (let i = 0; i < firstDay; i++) cells.push({ day: null, dateKey: null });
        for (let d = 1; d <= daysInMonth; d++) {
            const dateKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const status = dateSetByKey.get(dateKey);
            cells.push({ day: d, dateKey, status });
        }
        return cells;
    }, [currentYear, currentMonth, dateSetByKey]);

    const upcomingList = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        return departureDates
            .filter((e) => e.date >= today)
            .slice(0, 12)
            .map((e) => ({ ...e, label: formatDateLabel(e.date, lang) }));
    }, [departureDates, lang]);

    const statusConfig = {
        available: {
            class: 'bg-emerald-500 text-white',
            badge: 'bg-emerald-500 text-white',
            label: getVariable('departure_dates_status_available', 'Available'),
        },
        discounted: {
            class: 'bg-amber-500 text-white',
            badge: 'bg-amber-500 text-white',
            label: getVariable('departure_dates_status_discounted', 'Discounted'),
        },
        stop_sale: {
            class: 'bg-alsero-first text-white',
            badge: 'bg-alsero-first text-white',
            label: getVariable('departure_dates_status_stop_sale', 'Stop Sale'),
        },
    };

    return (
        <div className="w-full">
            {resolvedTitle && (
                <h3 className="text-lg font-bold text-alsero-dark-gray mb-4">
                    {resolvedTitle}
                </h3>
            )}

            {/* View toggle: Takvim | Liste */}
            <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-500 mr-2">
                    {getVariable('departure_dates_view', 'View')}:
                </span>
                <div className="inline-flex p-0.5 rounded-xl bg-gray-100 border border-gray-200/80">
                    <button
                        type="button"
                        onClick={() => setViewMode('calendar')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${viewMode === 'calendar' ? 'bg-white text-alsero-first shadow-sm border border-gray-200' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        {getVariable('departure_dates_calendar_view', 'Calendar')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${viewMode === 'list' ? 'bg-white text-alsero-first shadow-sm border border-gray-200' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        {getVariable('departure_dates_list_view', 'List')}
                    </button>
                </div>
            </div>

            <AnimatePresence mode="wait">
                {viewMode === 'list' ? (
                    <motion.div
                        key="list"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                    >
                        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                            <h4 className="text-base font-bold text-alsero-dark-gray">
                                {getVariable('departure_dates_upcoming', 'Upcoming departures')}
                            </h4>
                        </div>
                        <ul className="divide-y divide-gray-200">
                            {upcomingList.length === 0 ? (
                                <li className="px-5 py-8 text-center text-gray-500 text-sm">
                                    {getVariable('departure_dates_no_upcoming', 'No upcoming departures.')}
                                </li>
                            ) : (
                                upcomingList.map((item) => {
                                    const status = statusConfig[item.status ?? 'available'];
                                    return (
                                        <li
                                            key={item.date}
                                            className="flex items-center justify-between gap-4 px-5 py-3.5 hover:bg-gray-50/80 transition-colors"
                                        >
                                            <span className="text-alsero-dark-gray font-medium">
                                                {item.label}
                                            </span>
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${status.badge}`}>
                                                {status.label}
                                            </span>
                                        </li>
                                    );
                                })
                            )}
                        </ul>
                    </motion.div>
                ) : (
                    <motion.div
                        key="calendar"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="grid grid-cols-1 lg:grid-cols-1 gap-4 items-start"
                    >
                        {/* Takvim kartı - kompakt, tek renk */}
                        <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="flex items-center justify-between px-2 py-2 border-b border-gray-200 bg-gray-50">
                                <button
                                    type="button"
                                    onClick={goPrev}
                                    disabled={!canPrev}
                                    className="p-1.5 rounded text-alsero-dark-gray hover:bg-gray-200 disabled:opacity-40 disabled:pointer-events-none"
                                    aria-label={lang === 'tr' ? 'Önceki ay' : 'Previous month'}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={`${currentYear}-${currentMonth}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.15 }}
                                        className="text-sm font-semibold text-alsero-dark-gray min-w-[100px] text-center"
                                    >
                                        {monthNames[currentMonth - 1]} {currentYear}
                                    </motion.span>
                                </AnimatePresence>
                                <button
                                    type="button"
                                    onClick={goNext}
                                    disabled={!canNext}
                                    className="p-1.5 rounded text-alsero-dark-gray hover:bg-gray-200 disabled:opacity-40 disabled:pointer-events-none"
                                    aria-label={lang === 'tr' ? 'Sonraki ay' : 'Next month'}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>

                            <div className="grid grid-cols-7 border-b border-gray-200">
                                {weekdayNames.map((name) => (
                                    <div
                                        key={name}
                                        className="py-2 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wide"
                                    >
                                        {name}
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-7 p-2 gap-1">
                                {calendarDays.map((cell, idx) => (
                                    <div
                                        key={idx}
                                        className="min-h-[42px] flex items-center justify-center"
                                    >
                                        {cell.day === null ? (
                                            <span className="w-full" />
                                        ) : (
                                            <span
                                                className={`
                                                    w-full min-h-[38px] flex items-center justify-center rounded-md text-sm font-medium
                                                    ${cell.status
                                                        ? statusConfig[cell.status]?.class ?? statusConfig.available.class
                                                        : 'text-gray-400 bg-transparent'
                                                    }
                                                `}
                                            >
                                                {cell.day}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="px-2 py-2 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-center gap-3 text-[10px]">
                                {(['available', 'discounted', 'stop_sale'] as const).map((key) => (
                                    <span key={key} className="flex items-center gap-1">
                                        <span className={`w-2 h-2 rounded-sm ${statusConfig[key].badge}`} />
                                        <span className="text-gray-600">{statusConfig[key].label}</span>
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Yaklaşan çıkışlar - yan panel */}
                        <div className="hidden bg-white rounded-lg border border-gray-200 overflow-hidden h-fit">
                            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                                <h4 className="text-sm font-bold text-alsero-dark-gray">
                                    {getVariable('departure_dates_upcoming', 'Upcoming departures')}
                                </h4>
                            </div>
                            <ul className="divide-y divide-gray-200 max-h-[280px] overflow-y-auto">
                                {upcomingList.length === 0 ? (
                                    <li className="px-4 py-6 text-center text-gray-500 text-sm">
                                        {getVariable('departure_dates_no_upcoming', 'No upcoming departures.')}
                                    </li>
                                ) : (
                                    upcomingList.slice(0, 8).map((item) => {
                                        const status = statusConfig[item.status ?? 'available'];
                                        return (
                                            <li
                                                key={item.date}
                                                className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50/80 transition-colors"
                                            >
                                                <span className="text-sm text-alsero-dark-gray font-medium truncate">
                                                    {item.label}
                                                </span>
                                                <span className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${status.badge}`}>
                                                    {status.label}
                                                </span>
                                            </li>
                                        );
                                    })
                                )}
                            </ul>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
